export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function matchesSearchTokens(haystack: string, query: string): boolean {
  const normalizedHaystack = normalizeSearchText(haystack);
  const tokens = normalizeSearchText(query).split(/\s+/).filter(Boolean);

  if (tokens.length === 0) {
    return true;
  }

  return tokens.every((token) => normalizedHaystack.includes(token));
}

export interface SearchIndexEntry<T> {
  item: T;
  /** Normalized title, used to rank prefix matches first. */
  title: string;
  /** Normalized text the query tokens are matched against. */
  text: string;
}

/** Normalizes the searchable text of `items` once, for fast repeated search. */
export function buildSearchIndex<T>(
  items: T[],
  getTitle: (item: T) => string,
  getText: (item: T) => (string | null | undefined)[],
): SearchIndexEntry<T>[] {
  return items.map((item) => {
    const title = normalizeSearchText(getTitle(item));
    return {
      item,
      title,
      text: normalizeSearchText(
        [getTitle(item), ...getText(item)].filter(Boolean).join(" "),
      ),
    };
  });
}

/**
 * Items whose text contains every query token. Titles starting with the
 * query come first, then titles containing it, then other matches; the
 * original order is kept within each rank. Returns at most `limit` items
 * plus the total number of matches.
 */
export function searchIndex<T>(
  index: SearchIndexEntry<T>[],
  query: string,
  limit: number,
): { items: T[]; total: number } {
  const normalized = normalizeSearchText(query);
  const tokens = normalized.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { items: [], total: 0 };
  const ranked: [T[], T[], T[]] = [[], [], []];
  for (const entry of index) {
    if (!tokens.every((token) => entry.text.includes(token))) continue;
    const rank = entry.title.startsWith(normalized)
      ? 0
      : entry.title.includes(normalized)
        ? 1
        : 2;
    ranked[rank].push(entry.item);
  }
  const all = ranked.flat();
  return { items: all.slice(0, limit), total: all.length };
}
